import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import logger from '@widgets/logger';
import { useTranslation, useHubspotChat, useQueryParams } from '@hooks';
import { Typography } from '@components/Typography';
// import SplitButton from '@components/SplitButton';
import Button from '@components/Button';
import {
    SalesforceIcon,
    HubspotIcon,
    PipeDriveIcon,
    XeroIcon,
    QuickBooksIcon,
    GoogleIcon,
    ApiIcon,
    IFoodIcon,
    CoverManagerIcon,
    VTEXIcon,
    FacebookIcon,
    AppleIcon,
} from 'assets/images/integrations';
import { WhatsAppIcon } from 'assets/images/channels';
import zapier from 'assets/images/integrations/zapier.png';
import { GOOGLE_OAUTH_URL, AccIntegrationName, AccountIntegrationsDictionary } from '@constants/integrations';
import { ExternalRoutes, RoutePath } from '@constants/routes';
import {
    googleIntegrationService,
    ifoodIntegrationService,
    vtexIntegrationService,
    coverManagerIntegrationService,
    facebookIntegrationService,
    whatsappIntegrationService,
    appleIntegrationService,
} from '@services';
import { alertActions, accountActions, googleActions } from '@actions';
import { handleApiError } from '@helpers/api';
import ZapierModal from '@features/ZapierModal';
import FacebookSDK from '@widgets/facebook';
import WhatsAppSDK from '@widgets/whatsapp';
import IFoodModal from './IFoodModal';
import VTEXModal from './VTEXModal';
import CoverManagerModal from './CoverManagerModal';
import AppleModal from './AppleModal';
import { createProxyObject } from '@helpers/createProxyObject';
import { Container, Card, Status } from './styles';

const GOOGLE_ERROR = {
    CANCEL: 'access_denied',
};

const Integrations = () => {
    const { t } = useTranslation();

    const [zapierOpen, setZapierOpen] = useState(false);
    const [iFoodModalOpen, setIFoodModalOpen] = useState(false);
    const [vtexModalOpen, setVtexModalOpen] = useState(false);
    const [coverManagerModalOpen, setCoverManagerModalOpen] = useState(false);
    const [appleModalOpen, setAppleModalOpen] = useState(false);

    const account = useSelector(state => state.account?.account);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const { openHandler } = useHubspotChat();
    const query = useQueryParams();
    const history = useHistory();

    const ICONS = {
        [AccIntegrationName['API integration']]: <ApiIcon style={{ height: 40, width: 40 }} />,
        [AccIntegrationName.Zapier]: <img src={zapier} alt="zapier" style={{ height: 40, marginBottom: 8 }} />,
        [AccIntegrationName.Google]: <GoogleIcon />,
        [AccIntegrationName.Salesforce]: <SalesforceIcon />,
        [AccIntegrationName.Xero]: <XeroIcon />,
        [AccIntegrationName.Pipedrive]: <PipeDriveIcon />,
        [AccIntegrationName.Hubspot]: <HubspotIcon />,
        [AccIntegrationName.QuickBooks]: <QuickBooksIcon />,
        [AccIntegrationName.iFood]: <IFoodIcon />,
        [AccIntegrationName.VTEX]: <VTEXIcon />,
        [AccIntegrationName.CoverManager]: <CoverManagerIcon />,
        [AccIntegrationName.Facebook]: <FacebookIcon />,
        [AccIntegrationName.WhatsApp]: <WhatsAppIcon style={{ height: 40, width: 40 }} />,
        [AccIntegrationName.Apple]: <AppleIcon width={40} height={40} />,
    };

    const connectableIntegrations = [
        AccIntegrationName.Google,
        AccIntegrationName.Zapier,
        AccIntegrationName.iFood,
        AccIntegrationName.CoverManager,
        AccIntegrationName.VTEX,
        AccIntegrationName.Facebook,
        AccIntegrationName.WhatsApp,
        AccIntegrationName.Apple,
    ];

    const INTEGRATIONS_MAP = useMemo(() => {
        const CustomTextMap = createProxyObject({
            [AccIntegrationName['API integration']]: t('buttons.documentation'),
            default: t('buttons.contactSupport'),
        });
        const CustomClickMap = createProxyObject({
            [AccIntegrationName['API integration']]: () => window.open(ExternalRoutes.ApiIntegrationDocs[language]),
            default: openHandler,
        });
        return AccountIntegrationsDictionary.map(item => {
            const integration = account?.integrations.find(i => i.type === item.id);
            return {
                ...item,
                connected: integration?.isActive,
                hasIntegration: !!integration,
                canConnect: connectableIntegrations.includes(item.id),
                customText: CustomTextMap[item.id],
                customClick: CustomClickMap[item.id],
                // buttonType: item.id === AccIntegrationName.Facebook ? 'splitButton' : 'button',
            };
        });
    }, [account]);

    useEffect(() => {
        const code = query.get('code');
        const error = query.get('error');
        const state = query.get('state');
        const wabaId = query.get('waba_id');

        if (error === GOOGLE_ERROR.CANCEL) {
            history.replace(RoutePath.AccountSettingsIntegrations);
        } else if (code && account) {
            // Check if this is a WhatsApp callback (look for waba_id or state parameter)
            if (wabaId || state === 'whatsapp') {
                connectWhatsAppIntegration({ code });
            } else {
                connectGoogleIntegration(code);
            }
        }
    }, [query, history, account]);

    const connectGoogleIntegration = async code => {
        try {
            await googleIntegrationService.authenticateGoogleIntegration({
                accountId: account?.id,
                code,
                redirectUri: `${window.location.origin}${RoutePath.AccountSettingsIntegrations}`,
            });
            history.replace(RoutePath.AccountSettingsIntegrations);
            const locations = await dispatch(googleActions.get());
            if (locations.length === 0) {
                dispatch(alertActions.error(t('SettingsIntegrations.googleNoLocations')));
                await googleIntegrationService.revokeGoogleIntegration();
            } else {
                dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
                dispatch(accountActions.get());
            }
        } catch (error) {
            history.replace(RoutePath.AccountSettingsIntegrations);
            handleApiError(error, 'alertMessages.connectFail');
        }
    };

    const connectFacebookIntegration = async (data /* splitButtonState */) => {
        const { accessToken, expiresIn, userID /* code */ } = data;
        try {
            // if (splitButtonState === 0) {
            if (!accessToken) return;
            await facebookIntegrationService.authenticateFacebookIntegration({
                accessToken,
                expiresIn,
                userID,
            });
            // } else {
            //     if (!code) return;
            //     await facebookIntegrationService.authenticateMetaBusinessIntegration({
            //         code,
            //     });
            // }
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
            if (error.errorCode === 'FACEBOOK_INVALID_TOKEN') {
                logger.error('FACEBOOK_INVALID_TOKEN');
            }
        }
    };

    const connectWhatsAppIntegration = async data => {
        const { code } = data;
        try {
            if (!code) return;

            // Get embedded signup data from the message event listener
            const embeddedData = WhatsAppSDK.getEmbeddedSignupData();
            const wabaId = embeddedData?.data?.waba_id || '';
            const phoneNumberId = embeddedData?.data?.phone_number_id || '';
            const businessId = embeddedData?.data?.business_id || '';

            await whatsappIntegrationService.authenticateWhatsAppIntegration({
                code,
                wabaId,
                phoneNumberId,
                businessId,
                redirectUri: null,
            });

            // Clear the embedded signup data after successful integration
            WhatsAppSDK.clearEmbeddedSignupData();

            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
            logger.error('WhatsApp integration error:', error);
        }
    };

    const onConnectFacebook = async splitButtonState => {
        try {
            const { status } = await FacebookSDK.getLoginStatus();
            if (!status) return;

            // const response = splitButtonState === 0 ? await FacebookSDK.login() : await FacebookSDK.loginMetaBusiness();
            const response = await FacebookSDK.login();
            if (response.authResponse && response.status === 'connected') {
                connectFacebookIntegration(response.authResponse, splitButtonState);
            }
        } catch (error) {
            logger.error(error);
        }
    };

    const onConnectWhatsApp = async () => {
        try {
            const { status } = await WhatsAppSDK.getLoginStatus();
            if (!status) return;

            const response = await WhatsAppSDK.loginWhatsApp();

            // WhatsApp Embedded Flow might return the code in different ways
            if (response.code) {
                connectWhatsAppIntegration(response);
            } else if (response.authResponse && response.authResponse.code) {
                connectWhatsAppIntegration(response.authResponse);
            } else if (response.status === 'connected') {
                // Check if URL has code parameter
                const urlCode = query.get('code');
                if (urlCode) {
                    connectWhatsAppIntegration({ code: urlCode });
                }
            }
        } catch (error) {
            logger.error('WhatsApp connection error:', error);
        }
    };

    const IntegrationMapCb = {
        [AccIntegrationName.Google]: {
            apiCall: () => googleIntegrationService.revokeGoogleIntegration(),
            notConnectedCb: () => (window.location = GOOGLE_OAUTH_URL),
        },
        [AccIntegrationName.iFood]: {
            apiCall: () => ifoodIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setIFoodModalOpen(true),
        },
        [AccIntegrationName.VTEX]: {
            apiCall: () => vtexIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setVtexModalOpen(true),
        },
        [AccIntegrationName.CoverManager]: {
            apiCall: () => coverManagerIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setCoverManagerModalOpen(true),
        },
        [AccIntegrationName.Facebook]: {
            apiCall: () => facebookIntegrationService.disconnectIntegration(),
            notConnectedCb: splitButtonState => onConnectFacebook(splitButtonState),
        },
        [AccIntegrationName.WhatsApp]: {
            apiCall: () => {
                const integration = account?.integrations.find(i => i.type === AccIntegrationName.WhatsApp);
                return whatsappIntegrationService.disconnectIntegration(integration?.id);
            },
            notConnectedCb: () => onConnectWhatsApp(),
        },
        [AccIntegrationName.Apple]: {
            apiCall: () => appleIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setAppleModalOpen(true),
        },
    };

    const onClickIntegration = async (type, splitButtonState) => {
        const isConnected = INTEGRATIONS_MAP.find(i => i.id === type)?.connected;
        switch (type) {
            case AccIntegrationName.Google:
            case AccIntegrationName.iFood:
            case AccIntegrationName.VTEX:
            case AccIntegrationName.CoverManager:
            case AccIntegrationName.Facebook:
            case AccIntegrationName.WhatsApp:
            case AccIntegrationName.Apple:
                if (isConnected) {
                    try {
                        await IntegrationMapCb[type].apiCall();
                        dispatch(alertActions.success(t('alertMessages.disconnectIntegrationSuccess')));
                        dispatch(accountActions.get());
                    } catch (error) {
                        dispatch(alertActions.error(t('apiErrors.google_access_revoke_error')));
                    }
                } else {
                    IntegrationMapCb[type].notConnectedCb(splitButtonState);
                }
                return;
            case AccIntegrationName['API integration']:
                window.open(ExternalRoutes.ApiIntegrationDocs[language]);
                return;
            default:
                setZapierOpen(true);
                return;
        }
    };

    return (
        <>
            <Container>
                {INTEGRATIONS_MAP.map(item => (
                    <Card key={item.id} elevation={1}>
                        <span style={{ marginBottom: 8 }}>{ICONS[item.id]}</span>
                        <Typography variant="title1" sx={{ marginBottom: '16px', color: 'rgba(0, 0, 0, 0.87)' }}>
                            {item.name}
                        </Typography>
                        {account ? (
                            <Status
                                isConnected={item.connected}
                                isDisconnected={item.hasIntegration && !item.connected}
                                isHidden={!connectableIntegrations.includes(item.id)}
                            >
                                {item.hasIntegration
                                    ? item.connected
                                        ? t('SettingsIntegrations.connected')
                                        : t('SettingsCompanies.disconnected')
                                    : t('SettingsIntegrations.notConnected')}
                            </Status>
                        ) : (
                            <Status>...</Status>
                        )}
                        {item.canConnect ? (
                            // item.buttonType === 'button' || item.connected ? (
                            <Button
                                type="button"
                                variant={item.connected ? 'danger' : 'outlined'}
                                sx={{ marginTop: 'auto' }}
                                onClick={() => onClickIntegration(item.id)}
                            >
                                {item.connected ? t('buttons.disconnect') : t('buttons.connect')}
                            </Button>
                        ) : (
                            // ) : (
                            //     <SplitButton
                            //         variant="outlined"
                            //         options={[
                            //             {
                            //                 text: t('buttons.connect'),
                            //                 onClick: () => onClickIntegration(item.id, 0),
                            //             },
                            //             {
                            //                 text: t('buttons.connectWithMeta'),
                            //                 onClick: () => onClickIntegration(item.id, 1),
                            //             },
                            //         ]}
                            //     />
                            // )
                            <Button
                                type="button"
                                variant="outlined"
                                sx={{ marginTop: 'auto' }}
                                onClick={item.customClick}
                            >
                                {item.customText}
                            </Button>
                        )}
                    </Card>
                ))}
            </Container>
            <ZapierModal isOpen={zapierOpen} handleClose={() => setZapierOpen(false)} />
            {iFoodModalOpen ? <IFoodModal isOpen={iFoodModalOpen} onClose={() => setIFoodModalOpen(false)} /> : null}
            {vtexModalOpen ? <VTEXModal isOpen={vtexModalOpen} onClose={() => setVtexModalOpen(false)} /> : null}
            {coverManagerModalOpen ? (
                <CoverManagerModal isOpen={coverManagerModalOpen} onClose={() => setCoverManagerModalOpen(false)} />
            ) : null}
            {appleModalOpen ? <AppleModal isOpen={appleModalOpen} onClose={() => setAppleModalOpen(false)} /> : null}
        </>
    );
};

export default Integrations;
